import React, { useMemo } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { ReactComponent as TargetIcon } from "../../../../assets/icons/crosshair.svg";
import { Emoji } from "emoji-picker-react";
import AnimatedOrb from "../AnimatedOrb";
import SpeechBubble from "../SpeechBubble";
import CompactTaskItem from "../../../tasks/CompactTaskItem";

const TASKS_PER_PAGE = 5;

/**
 * Target selection step.
 * Slots shown = max(suggestedTargetCount, selectedTargets.length).
 * Clicking a task beyond current slots auto-expands — no "Add another" button.
 * Deselecting a task never leaves an empty placeholder — slots shrink to match.
 * A remove (×) button is shown on filled slots that are above suggestedTargetCount.
 */
const CAPACITY_SPEECH = {
  single: "What's the one thing we can get done today?",
  few: "What are we focusing on today? Just pick what feels right.",
  many: "Let's make the most of today. What are we taking on?",
};

const TargetStep = ({
  sortedTasks,
  selectedTargets,
  onToggleTarget,
  suggestedTargetCount,
  taskPage,
  onPageChange,
  onCreateNewTask,
  displayTaskCount,
  capacity,
}) => {
  // Slots = at least the capacity-defined count, grows as user selects more
  const totalSlots = Math.max(suggestedTargetCount, selectedTargets.length);
  const tasksPerPage = displayTaskCount || TASKS_PER_PAGE;

  const availableTasks = useMemo(
    () => sortedTasks.filter((t) => !t.completed),
    [sortedTasks],
  );

  const totalPages = Math.max(
    1,
    Math.ceil(availableTasks.length / tasksPerPage),
  );

  const pagedTasks = useMemo(() => {
    const start = taskPage * tasksPerPage;
    return availableTasks.slice(start, start + tasksPerPage);
  }, [availableTasks, taskPage, tasksPerPage]);

  const targetTasks = useMemo(
    () =>
      selectedTargets
        .map((id) => availableTasks.find((t) => (t.taskId || t.id) === id))
        .filter(Boolean),
    [selectedTargets, availableTasks],
  );

  return (
    // No scroller on the outer box — the body scroll container handles it.
    // Putting scroller here caused overflow-y: auto to clip the orb glow.
    <Box className="task-guide-step task-guide-step--targets">
      <AnimatedOrb size="medium" intensity="thinking" />
      <SpeechBubble>
        <Typography variant="body1" sx={{ margin: 0, textAlign: "center" }}>
          {CAPACITY_SPEECH[capacity] || CAPACITY_SPEECH.few}
        </Typography>
      </SpeechBubble>

      {/* Target slots */}
      <Box className="task-guide-targets__slots">
        {[...Array(totalSlots)].map((_, i) => {
          const task = targetTasks[i];
          // Only show a remove button on filled extra slots (above capacity count)
          const isExtra = i >= suggestedTargetCount;
          return (
            <Box
              key={i}
              className={`task-guide-targets__slot ${task ? "task-guide-targets__slot--filled" : ""}`}
            >
              {task ? (
                <>
                  <CheckCircleIcon
                    sx={{ fontSize: 20, color: "#429254", flexShrink: 0 }}
                  />
                  {task.emoji && (
                    <span
                      style={{
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Emoji unified={task.emoji} size={16} />
                    </span>
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#333",
                      fontWeight: 500,
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      flex: 1,
                    }}
                  >
                    {task.name || task.title}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => onToggleTarget(task.taskId || task.id)}
                    sx={{
                      color: isExtra ? "#aaa" : "#ccc",
                      padding: "2px",
                      flexShrink: 0,
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </>
              ) : (
                <>
                  <TargetIcon
                    style={{
                      width: 18,
                      height: 18,
                      stroke: "rgba(0, 0, 0, 0.3)",
                      fill: "transparent",
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    variant="body2"
                    sx={{
                      color: "#777",
                      flex: 1,
                      fontSize: "0.85rem",
                      letterSpacing: "0.02em",
                    }}
                  >
                    Empty target
                  </Typography>
                </>
              )}
            </Box>
          );
        })}
      </Box>

      {/* Task list — scroller lives here so only this section scrolls internally if needed */}
      <Box className="task-guide-targets__task-list scroller">
        <Typography
          variant="body2"
          sx={{ fontWeight: 600, mb: 1, color: "#004a3c", fontSize: "0.9rem" }}
        >
          Suggested tasks
        </Typography>

        {pagedTasks.map((task) => {
          const taskId = task.taskId || task.id;
          const isSelected = selectedTargets.includes(taskId);
          return (
            <CompactTaskItem
              key={taskId}
              task={task}
              selected={isSelected}
              onClick={() => onToggleTarget(taskId)}
            />
          );
        })}

        {/* Pagination */}
        <Box className="task-guide-targets__pagination">
          <IconButton
            size="small"
            disabled={taskPage === 0}
            onClick={() => onPageChange(taskPage - 1)}
          >
            <ArrowBackIcon fontSize="small" />
          </IconButton>
          <Typography variant="caption" sx={{ color: "#888" }}>
            {taskPage + 1} / {totalPages}
          </Typography>
          <IconButton
            size="small"
            disabled={taskPage >= totalPages - 1}
            onClick={() => onPageChange(taskPage + 1)}
          >
            <ArrowForwardIcon fontSize="small" />
          </IconButton>
        </Box>
      </Box>

      {/* Create new task option */}
      <Box className="task-guide-step__create-another-button" onClick={onCreateNewTask} sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ margin: 0, fontWeight: 500 }}>
          + Create a new task
        </Typography>
      </Box>
    </Box>
  );
};

export default TargetStep;
